import { Button } from "primereact/button";
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import {
  IDraftMessage,
  IDraftRunDetail,
} from "../../../store/queries/contractModelDrafts";
import {
  COMPOSER_ARIA,
  COMPOSER_NEWLINE_HINT,
  COMPOSER_PLACEHOLDER,
  CONVERSATION_ARIA,
  CONVERSATION_EMPTY,
  CONVERSATION_TITLE,
  INITIAL_INSTRUCTIONS_CHIP,
  MESSAGE_ABOUT_ONE_ITEM,
  MESSAGE_QUEUED_CHIP,
  MESSAGE_SENDING,
  QUESTIONS_FOR_YOU,
  QUEUED_COMPOSER_HINT,
  REGENERATE,
  REGENERATE_TOOLTIP,
  REMOVE_QUEUED_MESSAGE,
  RETRY,
  RETRY_TOOLTIP_GENERATE,
  RETRY_TOOLTIP_REGENERATE,
  RUN_DEAD_TITLE,
  RUN_ERROR_DETAILS,
  RUN_ERROR_HIDE_DETAILS,
  RUN_FAILED_TITLE,
  SEND_MESSAGE,
  messageOnStep,
  versionSeparator,
} from "../copy";
import {
  canRetryRun,
  composerClosedReason,
  humanizeRunError,
  inPlaceRevision,
  isFailedRun,
  latestVersionHoldsModel,
  retryModeOf,
} from "../runState";
import { isProcessing } from "../utils";
import ChatMarkdown from "./chatMarkdown";
import {
  EMPTY_LIVE_ACTIVITY,
  EMPTY_LIVE_REPLY,
  ILiveActivityMemory,
  ILiveReplyMemory,
  buildThread,
  collectOpenQuestions,
  formatThreadTime,
  isQueuedMessage,
  rememberActivity,
  rememberNarration,
  revisionProgressLine,
  stepTitle,
} from "./conversation";
import { promptsToShow } from "./conversationPrompts";
import LiveReply from "./LiveReply";
import { StepId } from "./steps";

/** Side by side from here up; below it the conversation drops under the wizard and can be collapsed. */
const WIDE = "1200px";

/**
 * CLE-2811 — the conversation's width when docked: about a third of the screen, never narrower than a
 * comfortable reading column nor wider than one. It is the most important thing on the page — the reviewer
 * uses it the way she used ChatGPT.
 */
const CHAT_WIDTH = "clamp(480px, 34vw, 640px)";

/** The wizard's step rail beside it. Its labels wrap; the locked "Rates & fee schedules" row still fits. */
const RAIL_WIDTH = "200px";

/**
 * The page layout when a conversation is shown: the wizard on the left, the conversation docked on the
 * right. Exported so the page wraps its wizard in it rather than re-deriving the breakpoint.
 */
export const ChatLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: ${WIDE}) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

export const ChatMain = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

/**
 * The step rail's column inside the wizard's PrimeFlex grid (`col-12 md:col-3`). Side by side with the
 * conversation — from WIDE up — it is a fixed 200px and the wizard content takes every remaining pixel;
 * below that it keeps its quarter. Doubled `&&` so it wins over PrimeFlex's width classes whatever order
 * the stylesheets load in.
 */
export const StepRailColumn = styled.div`
  @media (min-width: ${WIDE}) {
    && {
      flex: 0 0 ${RAIL_WIDTH};
      width: ${RAIL_WIDTH};
      max-width: ${RAIL_WIDTH};
    }
  }
`;

/** The wizard content beside the rail (`col-12 md:col-9`): from WIDE up, whatever the 200px rail leaves. */
export const WizardColumn = styled.div`
  min-width: 0;

  @media (min-width: ${WIDE}) {
    && {
      flex: 1 1 0;
      width: auto;
      min-width: 0;
    }
  }
`;

/**
 * Docked, the panel fills the viewport under the fixed navbar and stops above the fixed "Powered by"
 * badge in the bottom-right corner, which would otherwise sit on the send button.
 */
const Panel = styled.aside`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  background: var(--surface-card);

  @media (min-width: ${WIDE}) {
    flex: 0 0 ${CHAT_WIDTH};
    width: ${CHAT_WIDTH};
    position: sticky;
    top: calc(var(--navbar-height, 80px) + 1rem);
    height: calc(
      100vh - var(--navbar-height, 80px) - 1rem -
        var(--powered-by-badge-height, 48px) - 2 *
        var(--powered-by-badge-offset, 20px)
    );
    min-height: 24rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem 0.5rem 1rem;
  border-bottom: 1px solid var(--surface-border);
`;

/** Collapsing only exists on narrow screens — docked on the right, the panel is always open. */
const CollapseToggle = styled(Button)`
  @media (min-width: ${WIDE}) {
    display: none !important;
  }
`;

const Body = styled.div<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1 1 auto;

  @media (max-width: calc(${WIDE} - 1px)) {
    display: ${({ $collapsed }) => ($collapsed ? "none" : "flex")};
  }
`;

/**
 * The ONE scroll container. Messages never scroll on their own: a long reply renders in full and the
 * whole thread scrolls, like any chat.
 */
const Thread = styled.div`
  flex: 1 1 auto;
  min-height: 8rem;
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem 1rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: ${WIDE}) {
    max-height: none;
  }
`;

/** Hers: a light rounded bubble on the right. */
const UserBubble = styled.div<{ $muted?: boolean }>`
  align-self: flex-end;
  max-width: 85%;
  min-width: 0;
  padding: 0.55rem 0.9rem;
  border-radius: 18px;
  background: var(--surface-100, #f5f5f5);
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  opacity: ${({ $muted }) => ($muted ? 0.65 : 1)};
`;

/** The AI's: plain text on the panel, full width, a small mark on the left. */
const AssistantRow = styled.div`
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr);
  column-gap: 0.65rem;
  align-items: start;
`;

const Avatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  margin-top: 0.05rem;
  border-radius: 50%;
  border: 1px solid var(--color-purple-2, #cecafe);
  background: var(--purple-50, #fbf7ff);
  color: var(--primary-color);
`;

const Message = styled.div<{ $mine: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $mine }) => ($mine ? "flex-end" : "stretch")};
  gap: 0.25rem;
  min-width: 0;
`;

const Meta = styled.div<{ $mine: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: ${({ $mine }) => ($mine ? "flex-end" : "flex-start")};
  padding-left: ${({ $mine }) => ($mine ? "0" : "2.15rem")};
  font-size: 0.7rem;
  color: var(--text-color-secondary);
`;

const Chip = styled.span<{ $tone?: "warn" }>`
  padding: 0 0.4rem;
  border-radius: 999px;
  background: ${({ $tone }) =>
    $tone === "warn" ? "var(--orange-50, #fff7ed)" : "var(--surface-100)"};
  color: ${({ $tone }) =>
    $tone === "warn"
      ? "var(--orange-700, #c2410c)"
      : "var(--text-color-secondary)"};
  font-size: 0.7rem;
`;

/** "Version 2 · 09:24" — where a pass started. */
const Separator = styled.div`
  align-self: center;
  font-size: 0.7rem;
  color: var(--text-color-secondary);
  opacity: 0.85;
`;

const Composer = styled.div`
  padding: 0.5rem 0.75rem 0.6rem;
`;

/** CLE-2811 — the suggested prompts: small, wrapping, never wider than the panel. */
const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
`;

const Suggestion = styled.button`
  padding: 0.2rem 0.6rem;
  border: 1px solid var(--surface-border);
  border-radius: 999px;
  background: var(--surface-0, #ffffff);
  color: var(--text-color);
  font: inherit;
  font-size: 0.75rem;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

/** The composer: one rounded pill across the panel, the send button inside it on the right. */
const Pill = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  padding: 0.4rem 0.4rem 0.4rem 1rem;
  border: 1px solid var(--surface-border);
  border-radius: 24px;
  background: var(--surface-50, #fafafa);
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus-within {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(91, 77, 253, 0.12);
  }
`;

/** One line to six, then it scrolls — a composer is the one place a scroll of its own is right. */
const COMPOSER_MAX_LINES = 6;

const TextArea = styled.textarea`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 1.5em;
  max-height: calc(${COMPOSER_MAX_LINES} * 1.5em);
  margin: 0.3rem 0;
  padding: 0;
  border: 0;
  outline: none;
  resize: none;
  background: transparent;
  color: var(--text-color);
  font: inherit;
  font-size: 0.9rem;
  line-height: 1.5;
  overflow-y: auto;

  &::placeholder {
    color: var(--text-color-secondary);
  }
`;

const SendButton = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--primary-color);
  color: #ffffff;
  cursor: pointer;
  transition: opacity 0.15s;

  &:disabled {
    background: var(--surface-300, #e0e0e0);
    color: var(--surface-0, #ffffff);
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
`;

const Hint = styled.small`
  display: block;
  margin-top: 0.3rem;
  padding-left: 1rem;
  font-size: 0.7rem;
  color: var(--text-color-secondary);
`;

/** CLE-2811 — the raw error under a failed pass's bubble, for whoever debugs it. */
const RawError = styled.pre`
  margin: 0.5rem 0 0;
  padding: 0.5rem 0.65rem;
  border-radius: 6px;
  background: var(--surface-100, #f5f5f5);
  font-size: 0.75rem;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-height: 12rem;
  overflow-y: auto;
`;

/**
 * CLE-2811 — a failed or dead pass, said in the conversation like any other reply: what went wrong in
 * plain words (`humanizeRunError`), the raw message behind "Details", and Retry — which regenerates when
 * there is a model to diff against and generates otherwise (`retryModeOf`; the page makes the call).
 */
function RunErrorReply({
  run,
  onRetry,
  retrying,
}: {
  run: IDraftRunDetail;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const { summary, details } = humanizeRunError(run.lastError);
  const retry = onRetry && canRetryRun(run);
  return (
    <div className="text-sm line-height-3" data-testid="run-error">
      <div className="font-semibold mb-1">
        <i
          className="pi pi-exclamation-triangle mr-2"
          style={{ color: "var(--red-600)", fontSize: "0.85rem" }}
          aria-hidden
        />
        {run.status === "dead" ? RUN_DEAD_TITLE : RUN_FAILED_TITLE}
      </div>
      <p className="m-0">{summary}</p>
      {retry || details ? (
        <div className="flex align-items-center flex-wrap gap-2 mt-2">
          {retry ? (
            <Button
              label={RETRY}
              icon="pi pi-refresh"
              className="p-button-sm"
              loading={retrying}
              disabled={retrying}
              tooltip={
                retryModeOf(run) === "regenerate"
                  ? RETRY_TOOLTIP_REGENERATE
                  : RETRY_TOOLTIP_GENERATE
              }
              tooltipOptions={{ position: "top" }}
              onClick={onRetry}
            />
          ) : null}
          {details ? (
            <Button
              label={open ? RUN_ERROR_HIDE_DETAILS : RUN_ERROR_DETAILS}
              icon={open ? "pi pi-chevron-up" : "pi pi-chevron-down"}
              iconPos="right"
              className="p-button-text p-button-sm"
              aria-expanded={open}
              aria-controls={detailsId}
              onClick={() => setOpen((v) => !v)}
            />
          ) : null}
        </div>
      ) : null}
      {open && details ? <RawError id={detailsId}>{details}</RawError> : null}
    </div>
  );
}

/** A small four-point sparkle: the AI's mark. Inline, so no icon dependency. */
function Sparkle() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
      <path
        fill="currentColor"
        d="M11 2l1.9 6.1L19 10l-6.1 1.9L11 18l-1.9-6.1L3 10l6.1-1.9zM19 14l.9 2.1L22 17l-2.1.9L19 20l-.9-2.1L16 17l2.1-.9z"
      />
    </svg>
  );
}

/** Within this many pixels of the bottom counts as "reading the newest message". */
const STICK_THRESHOLD_PX = 48;

interface IProps {
  run: IDraftRunDetail;
  /** The wizard step she is on — sent with her message as a hint to the agent about what she means. */
  activeStep: StepId;
  regenerating: boolean;
  onRegenerate: () => void;
  onDelete: (messageId: string) => void;
  /** Resolves with what the server did, or null when it failed (the page has already toasted). */
  onSend: (
    body: string,
    stepId: StepId
  ) => Promise<{ queuedOnly: boolean } | null>;
  /** CLE-2811 — run a failed or dead pass again. Absent: the error bubble offers no Retry. */
  onRetry?: () => void;
  retrying?: boolean;
  /**
   * CLE-2811 (in place) — stop a REVISION pass: the page's stop (`POST …/stop`, no dialog), as the progress strip's
   * Stop — the run goes back to the model on screen and stays open. The strip is not shown during a revision pass, so
   * the live reply carries the Stop link. Absent: no Stop link. `stopping`: the request is in flight.
   */
  onStop?: () => void;
  stopping?: boolean;
}

interface IOptimistic {
  body: string;
  stepId: StepId;
  /** The ids the run held when she sent — the bubble goes once a NEW message with her text arrives. */
  knownIds: Set<string>;
}

/**
 * CLE-2811 — the run as ONE conversation, like working with ChatGPT.
 *
 * She types and presses Enter; the agent re-derives the model and answers in words (the worker's
 * `reply`, or its change reasons). While a pass runs, its reply is written into the thread as it arrives
 * (`LiveReply`), and she can keep typing: those messages queue and the server fires the next pass on its
 * own when this one finishes, so nothing waits on her coming back. The "Regenerate" button stays for the
 * one case with nothing to say — "I uploaded a document, rerun".
 */
function ConversationPanel({
  run,
  activeStep,
  regenerating,
  onRegenerate,
  onDelete,
  onSend,
  onRetry,
  retrying = false,
  onStop,
  stopping = false,
}: IProps) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [optimistic, setOptimistic] = useState<IOptimistic | null>(null);
  const [queuedHint, setQueuedHint] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const stickRef = useRef(true);
  const liveMemory = useRef<ILiveReplyMemory>(EMPTY_LIVE_REPLY);
  const activityMemory = useRef<ILiveActivityMemory>(EMPTY_LIVE_ACTIVITY);

  const working = regenerating || retrying || isProcessing(run.status);
  // The AI's words for the pass in flight — kept through `worker_returned`, forgotten after (pure: see
  // `rememberNarration`; folding it in during render is idempotent). The activity feed likewise: it is
  // what the live reply shows when the pass streams steps rather than text.
  liveMemory.current = rememberNarration(liveMemory.current, run);
  activityMemory.current = rememberActivity(activityMemory.current, run);
  const liveText = working ? liveMemory.current.text : "";
  const liveActivity = working ? activityMemory.current.lines : [];
  // CLE-2811 (in place) — a facts pass on a run that already has a model: the cards stay and the page shows no
  // progress strip, so the live reply says what the pass is doing ("Updating the Cigna PPO inpatient
  // reimbursement…") and offers Stop. A full-stage pass keeps the strip, and this bubble as before.
  const revision = inPlaceRevision(run);
  const liveLine = revision ? revisionProgressLine(run) : null;
  // CLE-2811 — a failed or dead pass is said in the thread, with Retry; a canceled run (or a dead one,
  // whose messages no pass would pick up) closes the composer with a line saying why.
  const failed = !working && isFailedRun(run.status);
  const closedReason = composerClosedReason(run);

  // CLE-2811 — the run instructions open the thread as her first bubble (they used to be a card above).
  const thread = useMemo(
    () =>
      buildThread(run.messages, run.versions ?? [], {
        runId: run.id,
        body: run.generationInstructions,
      }),
    [run.messages, run.versions, run.id, run.generationInstructions]
  );
  const hasConversation = thread.some((i) => i.kind !== "instructions");
  const suggestions = closedReason ? [] : promptsToShow(run.stage, draft);
  const questions = useMemo(
    () => (working ? [] : collectOpenQuestions(run.recommendations)),
    [working, run.recommendations]
  );
  const anyQueued = run.messages.some(isQueuedMessage);
  const echoed =
    optimistic !== null &&
    run.messages.some(
      (m) => !optimistic.knownIds.has(m.id) && m.body === optimistic.body
    );
  const showOptimistic = optimistic !== null && !echoed;

  // Drop the optimistic bubble as soon as the server's copy of her message is in the run.
  useEffect(() => {
    if (echoed) setOptimistic(null);
  }, [echoed]);

  // The hint is about messages waiting on a pass; once nothing is waiting, it has nothing to say.
  useEffect(() => {
    if (!anyQueued) setQueuedHint(false);
  }, [anyQueued]);

  // Keep the newest thing in view, like any chat — unless she has scrolled up to read something.
  useLayoutEffect(() => {
    const el = threadRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [
    thread.length,
    showOptimistic,
    working,
    questions.length,
    liveText,
    liveActivity.length,
    liveLine,
    failed,
  ]);

  const onThreadScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    stickRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD_PX;
  };

  // One line to six: grow with what she types.
  useLayoutEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending || closedReason) return;
    stickRef.current = true;
    setOptimistic({
      body,
      stepId: activeStep,
      knownIds: new Set(run.messages.map((m) => m.id)),
    });
    setDraft("");
    setSending(true);
    try {
      const result = await onSend(body, activeStep);
      if (!result) {
        // Keep what she wrote — losing a paragraph to a network blip is the worst thing a chat can do.
        setOptimistic(null);
        setDraft(body);
        return;
      }
      setQueuedHint(result.queuedOnly);
    } finally {
      setSending(false);
    }
  };

  // A suggestion is a starting point: it goes in the composer for her to edit or send, never sent for her.
  const pickSuggestion = (text: string) => {
    setDraft(text);
    window.setTimeout(() => {
      const el = composerRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(text.length, text.length);
    }, 0);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter is a newline. Never mid-IME composition (accented / CJK input).
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const assistant = (key: string, content: React.ReactNode, meta?: string) => (
    <Message key={key} $mine={false}>
      <AssistantRow>
        <Avatar>
          <Sparkle />
        </Avatar>
        <div className="min-w-0">{content}</div>
      </AssistantRow>
      {meta ? (
        <Meta $mine={false}>
          <span>{meta}</span>
        </Meta>
      ) : null}
    </Message>
  );

  const renderMessage = (m: IDraftMessage, queued: boolean) => {
    if (m.role === "assistant")
      return assistant(
        m.id,
        <ChatMarkdown text={m.body} />,
        formatThreadTime(m.createdAt)
      );
    const where = stepTitle(m.stepId);
    return (
      <Message key={m.id} $mine>
        <UserBubble>{m.body}</UserBubble>
        <Meta $mine>
          {where ? <Chip>{messageOnStep(where)}</Chip> : null}
          {m.recommendationId ? <Chip>{MESSAGE_ABOUT_ONE_ITEM}</Chip> : null}
          {queued ? <Chip $tone="warn">{MESSAGE_QUEUED_CHIP}</Chip> : null}
          <span>{formatThreadTime(m.createdAt)}</span>
          {queued ? (
            // Only a message no pass has consumed can go — an applied one is part of how the model was
            // derived (the server refuses it with 409).
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-rounded p-button-sm p-0"
              style={{ width: "1.25rem", height: "1.25rem" }}
              aria-label={REMOVE_QUEUED_MESSAGE}
              tooltip="Remove"
              tooltipOptions={{ position: "top" }}
              onClick={() => onDelete(m.id)}
            />
          ) : null}
        </Meta>
      </Message>
    );
  };

  const optimisticWhere = optimistic ? stepTitle(optimistic.stepId) : null;

  return (
    <Panel aria-label={CONVERSATION_ARIA}>
      <Header>
        <i className="pi pi-comments text-color-secondary" />
        <span className="font-semibold">{CONVERSATION_TITLE}</span>
        <Button
          label={REGENERATE}
          icon="pi pi-refresh"
          className="p-button-text p-button-sm ml-auto"
          tooltip={REGENERATE_TOOLTIP}
          tooltipOptions={{ position: "left" }}
          // CLE-2811 — the server refuses a canceled run, and a run whose latest version has nothing in it
          // (a failed first pass, or a failed revision pass whose model is still on screen): Retry is the way on.
          disabled={
            working ||
            run.status === "canceled" ||
            !latestVersionHoldsModel(run)
          }
          onClick={onRegenerate}
        />
        <CollapseToggle
          icon={collapsed ? "pi pi-chevron-down" : "pi pi-chevron-up"}
          className="p-button-text p-button-sm p-button-rounded"
          aria-label={collapsed ? "Show conversation" : "Hide conversation"}
          onClick={() => setCollapsed((v) => !v)}
        />
      </Header>

      <Body $collapsed={collapsed}>
        <Thread
          ref={threadRef}
          onScroll={onThreadScroll}
          data-testid="conversation-thread"
        >
          {!hasConversation && !showOptimistic && !working && !failed ? (
            <p className="m-0 text-sm text-color-secondary line-height-3">
              {CONVERSATION_EMPTY}
            </p>
          ) : null}

          {thread.map((item) => {
            if (item.kind === "version") {
              return (
                <Separator key={item.key}>
                  {versionSeparator(
                    item.versionNo,
                    formatThreadTime(item.createdAt)
                  )}
                </Separator>
              );
            }
            if (item.kind === "instructions") {
              // Hers, but not a message row: no time, never queued, never deletable.
              return (
                <Message key={item.key} $mine>
                  <UserBubble>{item.body}</UserBubble>
                  <Meta $mine>
                    <Chip>{INITIAL_INSTRUCTIONS_CHIP}</Chip>
                  </Meta>
                </Message>
              );
            }
            return renderMessage(item.message, item.queued);
          })}

          {showOptimistic && optimistic ? (
            <Message $mine>
              <UserBubble $muted>{optimistic.body}</UserBubble>
              <Meta $mine>
                {optimisticWhere ? (
                  <Chip>{messageOnStep(optimisticWhere)}</Chip>
                ) : null}
                <span>{MESSAGE_SENDING}</span>
              </Meta>
            </Message>
          ) : null}

          {working
            ? assistant(
                "live-reply",
                <LiveReply
                  text={liveText}
                  activity={liveActivity}
                  run={run}
                  progressLine={liveLine}
                  onStop={revision ? onStop : undefined}
                  stopping={stopping}
                />
              )
            : null}

          {questions.length > 0
            ? assistant(
                "questions",
                <div className="text-sm line-height-3">
                  <div className="font-semibold mb-1">{QUESTIONS_FOR_YOU}</div>
                  <ul className="m-0 pl-3">
                    {questions.map((q) => (
                      <li key={q} className="mb-1">
                        {q}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            : null}

          {failed
            ? assistant(
                "run-error",
                <RunErrorReply
                  run={run}
                  onRetry={onRetry}
                  retrying={retrying}
                />
              )
            : null}
        </Thread>

        <Composer>
          {suggestions.length > 0 ? (
            <Suggestions aria-label="Suggested questions">
              {suggestions.map((text) => (
                <Suggestion
                  key={text}
                  type="button"
                  onClick={() => pickSuggestion(text)}
                >
                  {text}
                </Suggestion>
              ))}
            </Suggestions>
          ) : null}
          <Pill>
            <TextArea
              ref={composerRef}
              rows={1}
              placeholder={COMPOSER_PLACEHOLDER}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              aria-label={COMPOSER_ARIA}
              disabled={!!closedReason}
            />
            <SendButton
              type="button"
              aria-label={SEND_MESSAGE}
              title={SEND_MESSAGE}
              disabled={!!closedReason || sending || draft.trim().length === 0}
              onClick={send}
            >
              <i
                className={sending ? "pi pi-spin pi-spinner" : "pi pi-arrow-up"}
                style={{ fontSize: "0.85rem" }}
              />
            </SendButton>
          </Pill>
          <Hint>
            {closedReason ??
              (queuedHint && anyQueued && working
                ? QUEUED_COMPOSER_HINT
                : COMPOSER_NEWLINE_HINT)}
          </Hint>
        </Composer>
      </Body>
    </Panel>
  );
}

export default ConversationPanel;
